import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';
import CitySearch from './components/CitySearch';
import Board from './components/Board';
import PinDialog from './components/PinDialog';
import PinList from './components/PinList';
import AdSlot from './components/AdSlot';
import { loadBoard, saveBoard, shareUrl, DEFAULT_CITIES } from './lib/state';
import { dayStart, shiftDate, todayIn, weekdayDate } from './lib/time';
import { trackEvent } from './analytics';

const initial = loadBoard();

function App() {
  const [cities, setCities] = useState(initial.cities);
  const [pins, setPins] = useState(initial.pins);
  const home = cities[0];
  const [date, setDate] = useState(initial.date || (home ? todayIn(home.timezone) : new Date().toISOString().slice(0, 10)));
  const [now, setNow] = useState(Date.now());
  const [hover, setHover] = useState(null); // fraction 0..1 of the home day under the pointer
  const [draft, setDraft] = useState(null); // instant being pinned
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30e3);
    return () => clearInterval(id);
  }, []);

  useEffect(() => saveBoard(cities, pins), [cities, pins]);

  // Drop a ?b= share link from the address bar once it has been loaded.
  useEffect(() => {
    if (initial.fromLink) window.history.replaceState(null, '', window.location.pathname);
  }, []);

  const start = useMemo(() => (home ? dayStart(date, home.timezone) : Date.now()), [date, home]);

  const addCity = useCallback((city) => {
    setCities((cs) => (cs.some((c) => c.name === city.name) ? cs : [...cs, city]));
    trackEvent('add_city', { city_name: city.name, timezone: city.timezone });
  }, []);

  const removeCity = (name) => {
    setCities((cs) => cs.filter((c) => c.name !== name));
    trackEvent('remove_city', { city_name: name });
  };

  const makeHome = (name) => {
    setCities((cs) => {
      const c = cs.find((x) => x.name === name);
      return [c, ...cs.filter((x) => x.name !== name)];
    });
    trackEvent('set_home', { city_name: name });
  };

  const move = (name, dir) => {
    setCities((cs) => {
      const i = cs.findIndex((c) => c.name === name);
      const j = i + dir;
      if (j < 0 || j >= cs.length) return cs;
      const next = cs.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const savePin = (pin) => {
    setPins((ps) => [...ps.filter((p) => p.id !== pin.id), pin].sort((a, b) => a.t - b.t));
    setDraft(null);
    trackEvent('add_marker', { label_length: pin.label.length });
  };

  const removePin = (id) => setPins((ps) => ps.filter((p) => p.id !== id));

  const reset = () => {
    setCities(DEFAULT_CITIES);
    setPins([]);
    setDate(todayIn(DEFAULT_CITIES[0].timezone));
  };

  const share = async () => {
    const url = shareUrl(cities, date, pins);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      window.prompt('Copy this link', url);
    }
    trackEvent('share_board', { cities: cities.length, pins: pins.length });
  };

  const isToday = home && date === todayIn(home.timezone);

  return (
    <div className="app">
      <header className="topbar">
        <a className="brand" href="/" aria-label="Time Pinboard home">
          <span className="brand-mark" aria-hidden="true" />
          Time Pinboard
        </a>
        <CitySearch onAdd={addCity} exclude={cities} />
        <div className="topbar-actions">
          <button className="btn ghost" onClick={share} disabled={!cities.length}>
            {copied ? 'Link copied' : 'Share'}
          </button>
          <button className="btn ghost" onClick={reset} title="Back to New York, London and Tokyo">
            Reset
          </button>
        </div>
      </header>

      <div className="layout">
        <main className="board-wrap">
          <div className="datebar">
            <button className="btn icon" onClick={() => setDate((d) => shiftDate(d, -1))} aria-label="Previous day">‹</button>
            <label className="date-pick">
              <span className="date-label">{home ? weekdayDate(home.timezone, start + 12 * 3600e3) : date}</span>
              <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Pick a date" />
            </label>
            <button className="btn icon" onClick={() => setDate((d) => shiftDate(d, 1))} aria-label="Next day">›</button>
            {!isToday && home && (
              <button className="btn ghost small" onClick={() => setDate(todayIn(home.timezone))}>Today</button>
            )}
            {home && <span className="datebar-note">Aligned to {home.name}. Click any timeline to pin a time.</span>}
          </div>

          {cities.length ? (
            <Board
              cities={cities}
              start={start}
              now={now}
              pins={pins}
              hover={hover}
              onHover={setHover}
              onPick={(t) => setDraft({ id: Date.now(), t, label: '', color: 'blue' })}
              onEditPin={(p) => setDraft(p)}
              onRemove={removeCity}
              onHome={makeHome}
              onMove={move}
            />
          ) : (
            <div className="empty">Add a city above to start comparing time zones.</div>
          )}

          <PinList pins={pins} cities={cities} onEdit={setDraft} onRemove={removePin} onJump={(d) => setDate(d)} />
          <AdSlot slot="inline" className="ad-inline" />
        </main>

        <aside className="side">
          <AdSlot slot="sidebar" className="ad-side" />
        </aside>
      </div>

      {draft && (
        <PinDialog
          pin={draft}
          cities={cities}
          onSave={savePin}
          onDelete={pins.some((p) => p.id === draft.id) ? () => { removePin(draft.id); setDraft(null); } : null}
          onClose={() => setDraft(null)}
        />
      )}
    </div>
  );
}

export default App;
